/**
 * Revision Utilities
 *
 * Shared helpers for error normalization and issue status counting
 * across the revision tracking hook suite.
 */

import type { InlineIssue, IssueStatus } from '../types';

/** Callback signature for revision tracking error handlers */
export type TrackingErrorHandler = (error: Error, context: string) => void;

/**
 * Normalize an unknown error into a typed Error and message string.
 * Eliminates the repeated `err instanceof Error ? err.message : String(err)` pattern.
 */
export function normalizeError(err: unknown): { error: Error; message: string } {
  if (err instanceof Error) {
    return { error: err, message: err.message };
  }
  const message = String(err);
  return { error: new Error(message), message };
}

/**
 * Count issues matching a given status.
 * Uses reduce to avoid intermediate array allocation from filter().length.
 */
export function countByStatus(issues: InlineIssue[], status: IssueStatus): number {
  return issues.reduce((count, i) => count + (i.status === status ? 1 : 0), 0);
}
